import {AxiosChatInstance, AxiosPublicationsInstance, AxiosChatInstanceTwo, AxiosInstance} from "../api/AxiosInstance";
import { gql } from '@apollo/client';
import { apolloClient } from '../api/ApolloInstance';


const appendIfPresent = (formData, key, value) => {
  if (value === undefined || value === null) return;
  formData.append(key, String(value));
};

const toBlobFromDataUrl = (value, mimeType = "application/octet-stream") => {
  if (typeof value !== "string") return null;
  const trimmed = value.trim();
  if (!trimmed) return null;

  if (trimmed.startsWith("data:")) {
    const commaIndex = trimmed.indexOf(",");
    if (commaIndex <= -1) return null;

    const metadata = trimmed.slice(5, commaIndex);
    const content = trimmed.slice(commaIndex + 1);
    const isBase64 = metadata.includes(";base64");
    const resolvedMime = metadata.split(";")[0] || mimeType;

    if (!isBase64) {
      return new Blob([decodeURIComponent(content)], { type: resolvedMime });
    }

    const binary = atob(content);
    const bytes = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i += 1) {
      bytes[i] = binary.codePointAt(i) || 0;
    }
    return new Blob([bytes], { type: resolvedMime });
  }

  // Si viene base64 puro, lo tratamos como imagen.
  const binary = atob(trimmed);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i += 1) {
    bytes[i] = binary.codePointAt(i) || 0;
  }
  return new Blob([bytes], { type: mimeType });
};

const normalizeFiles = (publication = {}) => {
  if (Array.isArray(publication.files)) return publication.files;
  if (Array.isArray(publication.archivos)) return publication.archivos;
  return [];
};

const buildPublicationMultipartPayload = (publication = {}) => {
  if (publication instanceof FormData) return publication;

  const formData = new FormData();

  const idUsuario = publication?.IdUsuario ?? publication?.idUsuario;
  const comentario = publication?.Comentario ?? publication?.comentario ?? "";
  const ubicacion = publication?.Ubicacion ?? publication?.ubicacion ?? "";
  const usuariosArrobados =
    publication?.UsuariosArrobados ?? publication?.usuariosArrobados ?? [];

  appendIfPresent(formData, "IdUsuario", idUsuario);
  appendIfPresent(formData, "Comentario", comentario);
  appendIfPresent(formData, "Ubicacion", ubicacion);

  if (Array.isArray(usuariosArrobados)) {
    usuariosArrobados.filter(Boolean).forEach((userId) => {
      formData.append("UsuariosArrobados", String(userId));
    });
  } else if (usuariosArrobados) {
    formData.append("UsuariosArrobados", String(usuariosArrobados));
  }

  const files = normalizeFiles(publication);
  files.forEach((fileLike, index) => {
    if (!fileLike) return;

    if (typeof File !== "undefined" && fileLike instanceof File) {
      formData.append("Files", fileLike, fileLike.name || `image-${index}.jpg`);
      return;
    }

    const candidateName =
      fileLike?.filename || fileLike?.fileName || `image-${index}.jpg`;
    const candidateType =
      fileLike?.filetype || fileLike?.fileType || "image/jpeg";
    const blob =
      toBlobFromDataUrl(fileLike?.base64 || fileLike?.content, candidateType);

    if (blob) {
      formData.append("Files", blob, candidateName);
    }
  });

  return formData;
};

export const newPublication = async (publication) => {
  try {
    const payload = buildPublicationMultipartPayload(publication)
    const response = await AxiosPublicationsInstance.post("/api/crear/publicacion/multipart", payload)
    return response.data
  } catch (error) {
    throw error.response?.data || "New publish failed"
  }
};

export const loadImage = async (imgData) => {
  try {
    const response = await AxiosInstance.post("/api/image/push", imgData)
    return response.data
  } catch (error) {
    throw error.response?.data || "Failed upload publication photo"
  }
}

// El endpoint espera `UltimoIdPublicacion` y `Cantidad`. Antes se mandaba
// `IdPublicacion`, un nombre que el backend ignora: cada llamada equivalia a
// pedir solo con IdUsuario, asi que siempre devolvia la primera pagina de 5 y el
// cursor del feed propio no podia avanzar.
// Los dos parametros opcionales se omiten cuando no llegan, para que la primera
// pagina se pida tal cual la define el contrato: solo con IdUsuario.
export const getOwnPublications = async (userId, ultimoIdPublicacion, cantidad) => {
  try {
    const params = { IdUsuario: userId };
    if (ultimoIdPublicacion) params.UltimoIdPublicacion = ultimoIdPublicacion;
    if (cantidad) params.Cantidad = cantidad;

    const response = await AxiosPublicationsInstance.get("/api/feed/propio", { params })
    return response.data
  } catch (error) {
    throw error.response?.data || "Get own publications failed"
  }
};

export const getImagePublication = async (imgId) => {
  try {
    const response = await AxiosInstance.post("/api/image/pull", imgId)
    return response.data
  } catch (error) {
    throw error.response?.data || "Get image from publication failed"
  }
}

export const deletePublication = async (idPublication) => {
  try {
    const response = await AxiosPublicationsInstance.post("/api/pubs/del", idPublication)
    return response
  } catch (error) {
    throw error.response?.data || `Delete publication ${idPublication.IdPublicacion} failed`
  }
}

export const getOtherUserPublications = async (pubRequest) => {
  try {
    const {
      IdUsuario,
      IdAmigo,
      IdPublicacion,
      cantidad,
    } = pubRequest || {};

    const params = new URLSearchParams();
    if (IdUsuario) params.set("IdUsuario", IdUsuario);
    if (IdAmigo) params.set("IdAmigo", IdAmigo);
    if (IdPublicacion) params.set("IdPublicacion", IdPublicacion);
    if (cantidad != null && cantidad !== "") {
      params.set("cantidad", cantidad);
    }

    const query = params.toString();
    const url = query
      ? `/api/feed/usuarios?${query}`
      : "/api/feed/usuarios";

    const response = await AxiosPublicationsInstance.get(url);
    return response.data
  } catch (error) {
    throw error?.response || "Failed getting other user pubs"
  }
}

export const getInteractionPermissions = async (permissionsRequest) => {
  try {
    const response = await AxiosPublicationsInstance.post(
      "/api/obtener/permisos-interaccion",
      permissionsRequest,
    )
    return response.data
  } catch (error) {
    throw error?.response || "Failed getting interaction permissions"
  }
}

export const getMainPublications = async (
  userId,
  publicationId,
  UltimoIdCupon,
  cantidad,
) => {
  try {
    /* const params = new URLSearchParams({
      IdUsuario: userId,
    });

    if (publicationId && publicationId !== "00000000-0000-0000-0000-000000000000") {
      params.set("IdPublicacion", publicationId);
    }

    if (cantidad) {
      params.set("Cantidad", cantidad);
    } else {
      params.set("Cantidad", 50);
    }
    const response = await AxiosPublicationsInstance.get(
      `/api/feed/general?${params.toString()}`,
    )
    return response.data */

    const variables = {
      idUsuario: userId,
      cantidad: cantidad ? parseInt(cantidad, 10) : 50
    };

    if (publicationId && publicationId !== "00000000-0000-0000-0000-000000000000") {
      variables.idPublicacion = publicationId;
    }

    const response = await apolloClient.query({
      query: gql`
        query GetGeneralMainPublish($idUsuario: String!, $idPublicacion: String, $cantidad: Int) {
          generalMainPublish(
            idUsuario: $idUsuario
            idPublicacion: $idPublicacion
            cantidad: $cantidad
          ) {
            idUsuario
            idPublicacion
            fotoPerfilUrl
            nombres
            apellidos
            nickName
            comentario
            fechaRegistro
            ubicacion
            modificado
            visible
            idListaFav
            numReacciones
            numComentarios
            reaccionMia
            guardado
            lugarGuardado
            tipoRegistro
            idCupon
            couponTitle
            discountValue
            discountTypeName
            couponCode
            categoryName
            endDate
            alcance
            reacciones {
              nickname
              idusuario
            }
            imagenes {
              idImagen
              orden
              url
            }
            arrobados {
              nickname
              idusuario
            }
          }
        }
      `,
      variables: variables,
    });

    return response.data.generalMainPublish;
  } catch (error) {
    throw error?.response || "Failed getting main feed"
  }
}

export const savePublication = async (saveReq) => {
  try {
    const response = await AxiosChatInstance.post("/api/guardar/publicacion-favorita", saveReq)
    return response.data
  } catch (error) {
    throw error?.response || "Failed saving pub"
  }
}

export const reactPublication = async (reactReq) => {
  try {
    const response = await AxiosPublicationsInstance.post("/api/pub/reac", reactReq)
    return response.data
  } catch (error) {
    throw error?.response || "Failed reacting to publication"
  }
}

export const hidePublication = async (hideReq) => {
  try {
    const response = await AxiosChatInstance.post("/api/ocultar", hideReq)
    return response
  } catch (error) {
    throw error?.response || "Failed hiding pub"
  }
}

export const reportPublication = async (reportReq) => {
  try {
    const response = await AxiosChatInstance.post("/api/reportaPubs", reportReq)
    return response
  } catch (error) {
    throw error?.response || "Failed hiding pub"
  }
}

export const getSavedPublications = async (userId, listId) => {
  try {
    const response = await AxiosChatInstance.get(
      "/api/mostrar/publicaciones-favoritas",
      {
        params: {
          IdUsuario: userId,
          UltimoId: listId,
        },
      },
    )
    return response.data?.data ?? response.data
  } catch (error) {
    throw error.response?.data || "Get own publications failed"
  }
};

export const commentPublication = async (commentReq) => {
  try {
    const response = await AxiosChatInstance.post("/api/pubs/comment", commentReq)
    return response.data
  } catch (error) {
    throw error.response?.data || "Get own publications failed"
  }
};

export const getPublicationById = async (userId, publicationId) => {
  try {
    const response = await AxiosChatInstance.get(`/api/pubs/porId?IdUsuario=${userId}&IdPublicacion=${publicationId}`)
    return response.data?.data ?? response.data
  } catch (error) {
    throw error?.response || "Failed get publication by id"
  }
};

export const getListRecommendations = async (lang) => {
  try {
    const response = await AxiosInstance.get(`/api/lang/reco?ln=${lang}`)
    return response.data
  } catch (error) {
    throw error?.response || "Failed get list recomendations"
  }
};

export const getListPlacesOfInterest = async (idUsuario) => {
  try {
    const response = await AxiosChatInstance.get(`/api/recomendaciones/gmaps?IdUsuario=${idUsuario}`)
    return response.data
  } catch (error) {
    throw error?.response || "Failed get list places of interest"
  }
};

export const getRecomendations = async (query) => {
  try {
    const response = await AxiosChatInstance.get(`/api/sitios/interes?query=${query}`)
    return response.data
  } catch (error) {
    throw error?.response || "Failed get recomendations"
  }
};

export const getSavedRecomendations = async (id) => {
  try {
    const response = await AxiosInstance.get(`/api/mostrar/favrec?IdUsuario=${id}`)
    return response.data
  } catch (error) {
    throw error?.response || "Failed get recomendations"
  }
};

export const saveRecomendation = async (id, jsonValue) => {
  try {
    const response = await AxiosChatInstanceTwo.post(`api/guardar-sitios/${id}`, jsonValue)
    return response.data
  } catch (error) {
    throw error?.response || "Failed get save recomendation"
  }
};

export const notifySavedRecommendation = async (payload) => {
  try {
    const response = await AxiosChatInstance.post("/api/signr/saved-recommendation", payload)
    return response.data
  } catch (error) {
    throw error?.response || "Failed notify saved recommendation"
  }
};

export const postImageRecomendation = async (photo) => {
  try {
    const response = await AxiosChatInstance.post("/api/gplace/get", photo)
    return response.data
  } catch (error) {
    throw error?.response || "Failed get image recomendation"
  }
}

export const getPublicationComments = async (commentsReq) => {
  try {
    const response = await AxiosChatInstance.post("/api/pubs/getcomments", commentsReq)
    return response.data
  } catch (error) {
    throw error?.response || "Failed get comments"
  }
};

export const postPublicationReactions = async (reactionsReq) => {
  try {
    const response = await AxiosChatInstance.post("/api/pubs/reaccioncom", reactionsReq)
    return response.data
  } catch (error) {
    throw error?.response || "Failed get reactions"
  }
}

// Lista de a los que sigo, seguidores y conexiones
export const postListConnectionsAndFollowers = async (data) => {
  try {
    const response = await AxiosPublicationsInstance.post("/api/usr/lstusrs", data)
    return response.data
  } catch (error) {
    throw error?.response || "Failed list"
  }
}

// Obtener publicaciones hashtag
export const getHashtagPosts = async (data) => {
  try {
    const response = await AxiosPublicationsInstance.post("/api/feed/publicaciones-hashtag", data)
    return response.data
  } catch (error) {
    throw error?.response || "Failed get hashtag posts"
  }
}

// Obtener geo ubicaciones
export const getGeoLocations = async (idEntidad) => {
  try {
    const response = await AxiosChatInstance.get(`/api/geoubicacion/obtener?IdEntidad=${idEntidad}`)
    return response.data
  } catch (error) {
    throw error?.response || "Failed get geo locations"
  }
}

// Insertar o actualizar geo ubicación
export const postInsertOrUpdateGeolocation = async (data) => {
  try {
    const response = await AxiosChatInstance.post(`/api/geoubicacion/insert-act`, data)
    return response.data
  } catch (error) {
    throw error?.response || "Failed insert or update geolocation"
  }
}

// Editar comentario
export const postEditComment = async (data) => {
  try {
    const response = await AxiosChatInstance.post("/api/editar/comentario", data)
    return response.data
  } catch (error) {
    throw error?.response || "Failed edit comment"
  }
}

// Eliminar comentario
export const postDeleteComment = async (data) => {
  try {
    const response = await AxiosChatInstance.post("/api/eliminar/comentario", data)
    return response.data
  } catch (error) {
    throw error?.response || "Failed delete comment"
  }
}

// Menciones (arrobados)
export const suggestMentions = async (data) => {
  try {
    const response = await AxiosPublicationsInstance.post("/api/arrobar/usuario", data)
    return response.data
  } catch (error) {
    throw error?.response || "Failed suggest mentions"
  }
}

// Idiomas soportados por Google
export const getCatalogForGoogle = async (language) => {
  try {
    const response = await AxiosInstance.get(`/api/translate/supported?ui=${language}`)
    return response.data
  } catch (error) {
    throw error?.response || "Failed translate text"
  }
}

// Traducción de texto
export const translateText = async (data) => {
  try {
    const response = await AxiosInstance.post("/api/translate/text", data)
    return response.data
  } catch (error) {
    throw error?.response || "Failed translate text"
  }
}

// Traducción de textos dinámicos
export const translateDynamicText = async (domain, contentId) => {
  try {
    const response = await AxiosInstance.post(`/api/translate/dynamic?domain=${domain}&contentId=${contentId}`)
    return response.data
  } catch (error) {
    throw error?.response || "Failed translate text"
  }
}

// Busqueda de habitaciones
export const searchRooms = async (data) => {
  try {
    const response = await AxiosPublicationsInstance.post("/api/obtener/busqueda-housing", data)
    return response.data
  } catch (error) {
    throw error?.response || "Failed search rooms"
  }
}

// Obtener sugerencias housing
export const getHousingSuggestions = async () => {
  try {
    const response = await AxiosPublicationsInstance.get("/api/obtener/sugerencias-housing")
    return response.data
  } catch (error) {
    throw error?.response || "Failed get housing suggestions"
  }
}

export const getNomadSuggestions = async (idUsuario) => {
  try {
    const response = await AxiosPublicationsInstance.get(
      "/api/General/Sugerencias",
      {
        params: {
          IdUsuario: idUsuario,
        },
      },
    )
    return response.data
  } catch (error) {
    throw error?.response || "Failed get nomad suggestions"
  }
}

// Mostrar los cinco inmuebles cerca de la ubicación buscada
export const getNearbyHousing = async (data) => {
  try {
    const response = await AxiosPublicationsInstance.post("/api/busqueda/top5-cercanos", data)
    return response.data
  } catch (error) {
    throw error?.response || "Failed get nearby housing"
  }
}

// Autocompletar búsqueda
export const autocompleteSearch = async (query) => {
  try {
    const response = await AxiosChatInstance.get(`/api/autocompletar/busqueda?query=${query}`)
    return response.data
  } catch (error) {
    throw error?.response || "Failed autocomplete search"
  }
}

// Mostrar pin con ubicación
export const getLocationPin = async (data) => {
  try {
    const response = await AxiosPublicationsInstance.post("/api/mostrar/tarjeta-pin", data)
    return response.data
  } catch (error) {
    throw error?.response || "Failed get place details"
  }
}

// Obtener detalles de la habitación
export const getRoomDetails = async (data) => {
  try {
    const response = await AxiosPublicationsInstance.post("/api/obtener/detalles-pin", data)
    return response.data
  } catch (error) {
    throw error?.response || "Failed get room details"
  }
}

// Sincronizar el mapa con todos los filtros de búsqueda
export const syncMapWithFilters = async (data) => {
  try {
    const response = await AxiosPublicationsInstance.post("/api/motor/sincronizacion-filtros", data)
    return response.data
  } catch (error) {
    throw error?.response || "Failed sync map with filters"
  }
}
// Si quieres, te puedo hacer el recorrido exacto “desde qué pantalla se guarda address/geolocation y cómo termina impactando recommendationsProperties”.
